import React, { Suspense, lazy, useEffect } from "react"
import { Switch, Route, withRouter } from "react-router-dom"
import { connect } from "react-redux"
import { createStructuredSelector } from "reselect"
import { Spin, message } from "antd"
import { LoadingOutlined } from "@ant-design/icons"

import { GlobalStyles } from "./styles/global.styles"

import {
  selectIsAuthChecking,
  selectIsAuthenticated,
} from "./redux/user/user.selectors"
import { checkAuthStart, signOutStart } from "./redux/user/user.actions"
import ErrorBoundary from "./components/error-boundary/error-boundary.component"

let MainLayout = null
let LoginPage = null
let PlatformsListPage = null
let CategoriesListPage = null
let OptionsListPage = null
let EstimationsListPage = null
let EstimationPage = null
let ConfigurationPage = null

const antIcon = <LoadingOutlined style={{ fontSize: 56 }} spin />

const HomePage = lazy(() => import("./pages/home/home.container"))

if (!process.env.REACT_APP_WP) {
  MainLayout = lazy(() =>
    import("./components/main-layout/main-layout.component")
  )
  LoginPage = lazy(() => import("./pages/login/login.container"))
  PlatformsListPage = lazy(() =>
    import("./pages/platforms/platforms.container")
  )
  CategoriesListPage = lazy(() =>
    import("./pages/categories/categories.container")
  )
  OptionsListPage = lazy(() => import("./pages/options/options.container"))
  EstimationsListPage = lazy(() =>
    import("./pages/estimations/estimations.container")
  )
  EstimationPage = lazy(() => import("./pages/estimation/estimation.container"))
  ConfigurationPage = lazy(() =>
    import("./pages/configuration/configuration.container")
  )
}

const App = ({
  isAuthChecking,
  isAuthenticated,
  checkAuth,
  signOut,
  history: { location },
}) => {
  const isAdminRoute = location.pathname.includes("admin")
  const antMessageKey = "updatable"

  useEffect(() => {
    if (isAdminRoute) {
      checkAuth()
    }
  }, [checkAuth, isAdminRoute])

  useEffect(() => {
    if (isAuthChecking && isAdminRoute) {
      message.loading({ content: "Checking...", key: antMessageKey })
    } else if (isAdminRoute) {
      if (isAuthenticated) {
        message.success({
          content: "Great! Let's rock!",
          key: antMessageKey,
          duration: 2,
        })
      } else {
        message.error({
          content: "Please login!",
          key: antMessageKey,
          duration: 2,
        })
      }
    }
  }, [isAuthChecking, isAuthenticated, isAdminRoute])

  return (
    <>
      <GlobalStyles />
      <Switch>
        <ErrorBoundary>
          <Suspense
            fallback={
              <Spin
                className="page-loading-spinner-wrap"
                indicator={antIcon}
                size="large"
              />
            }
          >
            {!process.env.REACT_APP_WP ? (
              <Route
                path="/admin"
                render={(props) =>
                  isAuthenticated ? (
                    <MainLayout {...props} signOut={signOut}>
                      <Switch>
                        <Route
                          exact
                          path="/admin/platforms"
                          component={PlatformsListPage}
                        />
                        <Route
                          exact
                          path="/admin/categories"
                          component={CategoriesListPage}
                        />
                        <Route
                          exact
                          path="/admin/options"
                          component={OptionsListPage}
                        />
                        <Route
                          exact
                          path="/admin/estimations"
                          component={EstimationsListPage}
                        />
                        <Route
                          exact
                          path="/admin/estimations/:estimationID"
                          component={EstimationPage}
                        />
                        <Route
                          exact
                          path="/admin/configuration"
                          component={ConfigurationPage}
                        />
                      </Switch>
                    </MainLayout>
                  ) : (
                    <LoginPage {...props} isAuthChecking={isAuthChecking} />
                  )
                }
              />
            ) : (
              ""
            )}
            <Route
              path={!process.env.REACT_APP_WP ? "/" : "/calculator"}
              exact
              component={HomePage}
            />
          </Suspense>
        </ErrorBoundary>
      </Switch>
    </>
  )
}

const mapStateToProps = createStructuredSelector({
  isAuthChecking: selectIsAuthChecking,
  isAuthenticated: selectIsAuthenticated,
})

const mapDispatchToProps = (dispatch) => ({
  checkAuth: () => dispatch(checkAuthStart()),
  signOut: () => dispatch(signOutStart()),
})

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(App))
